import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiHelper';

import { useCompany } from '../../context/CompanyContext';
import PageHeader from '../components/shared/PageHeader';
import ErrorBoundary from '../../components/ErrorBoundary';
import LoadingSpinner from '../../components/LoadingSpinner';
import { showSuccess, showError, showInfo } from '../../utils/toastHelper';

function EmailTemplateSettings() {
  const { activeCompany } = useCompany();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [template, setTemplate] = useState({
    templateName: '',
    primaryColor: '#222222',
    secondaryColor: '#f4f4f4',
    tagline: '',
    taglineColor: '#ffffff',
    footerText: '',
    supportPhone: '',
    supportEmail: '',
    emailLogoUrl: null,
  });

  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (activeCompany?.id) {
      loadTemplate();
    }
  }, [activeCompany?.id]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/companies/${activeCompany.id}/email-template`);
      if (response.success && response.template) {
        setTemplate({
          templateName: response.template.templateName || '',
          primaryColor: response.template.primaryColor || '#222222',
          secondaryColor: response.template.secondaryColor || '#f4f4f4',
          tagline: response.template.tagline || '',
          taglineColor: response.template.taglineColor || '#ffffff',
          footerText: response.template.footerText || '',
          supportPhone: response.template.supportPhone || '',
          supportEmail: response.template.supportEmail || '',
          emailLogoUrl: response.template.emailLogoUrl || null,
        });
      }
      setError(null);
    } catch (err) {
      console.error('Failed to load email template:', err);
      setError('E-posta ayarları yüklenirken bir hata oluştu');
      showError('E-posta ayarları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setTemplate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { 
        showError("Logo dosyası 5MB'dan küçük olmalıdır");
        return;
      }
      
      const localUrl = URL.createObjectURL(file);
      const img = new Image();
      
      img.onload = () => {
        if (img.width > 800 || img.height > 800) {
          showError("Logo boyutları en fazla 800x800 piksel olmalıdır. Lütfen daha küçük bir görsel seçin.");
          URL.revokeObjectURL(localUrl);
          e.target.value = ''; // Reset input
          return;
        }
        
        setSelectedFile(file);
        setTemplate(prev => ({ ...prev, emailLogoUrl: localUrl }));
      };
      
      img.onerror = () => {
        showError("Geçerli bir görsel dosyası yükleyin.");
        URL.revokeObjectURL(localUrl);
        e.target.value = '';
      };
      
      img.src = localUrl;
    }
  };

  const removeLogoAndSetFlag = () => {
     setTemplate(prev => ({ ...prev, emailLogoUrl: null }));
     setSelectedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeCompany?.id) return;
    
    try {
      setSaving(true);
      const formData = new FormData();
      
      if (selectedFile) {
        formData.append('logoFile', selectedFile);
      } else if (template.emailLogoUrl === null) {
        formData.append('removeLogo', 'true');
      }

      if (template.templateName) formData.append('templateName', template.templateName);
      formData.append('primaryColor', template.primaryColor);
      formData.append('secondaryColor', template.secondaryColor);
      formData.append('tagline', template.tagline);
      formData.append('taglineColor', template.taglineColor);
      formData.append('footerText', template.footerText);
      formData.append('supportPhone', template.supportPhone);
      formData.append('supportEmail', template.supportEmail);

      const response = await apiRequest(`/companies/${activeCompany.id}/email-template`, {
        method: 'PUT',
        data: formData
      });

      if (response.success && response.template) {
        showSuccess('E-posta şablon ayarları başarıyla güncellendi.');
        // Update URL from the server response
        setTemplate(prev => ({ ...prev, emailLogoUrl: response.template.emailLogoUrl }));
        setSelectedFile(null); // Clear selected file context
        setIsEditing(false);
      } else {
         showError(response.message || 'Kaydedilirken hata oluştu.');
      }
    } catch (err) {
      console.error('Failed to save email template:', err);
      showError('Ayarlar kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  if (!activeCompany) {
     return <div className="p-4 text-muted">Şirket seçmelisiniz.</div>;
  }

  if (loading) {
    return (
      <div className="container-fluid p-0">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
          <LoadingSpinner message="Şablon ayarları yükleniyor..." />
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="container-fluid p-0" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <div className="sticky-top bg-white pt-2 pb-2" style={{ zIndex: 1020, top: '70px', borderBottom: isEditing ? '1px solid #dee2e6' : 'none' }}>
          <PageHeader
            title="E-posta Markalama"
            description="Müşterilerinize giden e-postaların görünümünü (renk, logo, bilgiler) buradan düzenleyin."
            breadcrumbs={[{ label: 'Dashboard', path: '/admin' }, { label: 'E-posta Ayarları' }]}
            actions={
              !isEditing ? (
                <button type="button" className="btn btn-primary" onClick={() => setIsEditing(true)}>
                  <i className="fas fa-edit me-2"></i>Düzenle
                </button>
              ) : (
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-outline-primary" onClick={() => { setIsEditing(false); loadTemplate(); }} disabled={saving}>
                    <i className="fas fa-times me-2"></i>İptal
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                    {saving ? 'Kaydediliyor...' : <><i className="fas fa-save me-2"></i>Kaydet</>}
                  </button>
                </div>
              )
            }
          />
        </div>

        <div className="row g-4 mt-2">
           {/* FORM SETTINGS (Left Side) */}
           <div className="col-12 col-xl-5">
              <div className="card border-0 shadow-sm">
                 <div className="card-header bg-white border-bottom py-3">
                    <h6 className="mb-0 fw-bold">Şablon Ayarları</h6>
                 </div>
                 <div className="card-body">
                    <form>
                       <div className="row g-3">
                          {/* Gönderici Adı */}
                          <div className="col-12">
                            <label className="form-label fw-semibold">
                               Gönderici / Şirket Adı
                               <span className="text-muted fw-normal ms-1" style={{ fontSize: '11px' }}>(logo yokken e-postada ve footer'da görünür)</span>
                             </label>
                             <input
                               type="text"
                               className="form-control"
                               placeholder="Gönderici adını girin"
                               value={template.templateName}
                               onChange={(e) => handleChange('templateName', e.target.value)}
                               disabled={!isEditing}
                             />
                             <small className="text-muted">Boş bırakırsanız sistem şirket adınızı otomatik kullanır.</small>
                           </div>

                           {/* Logo Upload */}
                           <div className="col-12 mb-3">
                              <label className="form-label fw-semibold w-100">
                                E-posta Logosu
                                <span className="text-muted fw-normal ms-1" style={{ fontSize: '11px' }}>(opsiyonel — header'da şirket adının yerine geçer)</span>
                              </label>
                              <div className="border rounded p-4 bg-light" style={{ position: 'relative', minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                 {template.emailLogoUrl ? (
                                    <>
                                       <img src={template.emailLogoUrl} alt="Email Logo" className="img-fluid" style={{ maxHeight: '80px' }} />
                                       {isEditing && (
                                          <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2" onClick={removeLogoAndSetFlag}>
                                            <i className="fas fa-trash"></i>
                                          </button>
                                       )}
                                    </>
                                 ) : (
                                    <div className="text-center text-muted">
                                       <div style={{ border: '2px dashed #adb5bd', borderRadius: '6px', padding: '16px 24px', display: 'inline-block' }}>
                                         <i className="fas fa-image fa-2x mb-2 d-block"></i>
                                         <div style={{ fontSize: '12px', fontWeight: 600 }}>Logo yüklenmemiş</div>
                                         <div style={{ fontSize: '11px' }}>Yerine <strong>"{template.templateName || activeCompany?.name}"</strong> metni kullanılır</div>
                                       </div>
                                    </div>
                                 )}
                              </div>
                              {isEditing && (
                                 <input type="file" className="form-control mt-2" accept="image/png,image/jpeg" onChange={handleLogoChange} />
                              )}
                              <small className="text-muted">Kare veya şeffaf PNG önerilir · maks. 800×800 px · 5 MB</small>
                           </div>

                          <div className="col-6">
                            <label className="form-label fw-semibold">Vurgu Rengi</label>
                            <input type="color" className="form-control form-control-color w-100" value={template.primaryColor} onChange={(e) => handleChange('primaryColor', e.target.value)} disabled={!isEditing} />
                            <small className="text-muted">CTA butonu ve başlık alt çizgisi</small>
                          </div>
                          <div className="col-6">
                            <label className="form-label fw-semibold">Başlık Arka Planı</label>
                            <input type="color" className="form-control form-control-color w-100" value={template.secondaryColor} onChange={(e) => handleChange('secondaryColor', e.target.value)} disabled={!isEditing} />
                            <small className="text-muted">E-posta üst bölüm arka planı</small>
                          </div>

                          <div className="col-9">
                             <label className="form-label fw-semibold">Slogan (Tagline)</label>
                             <input type="text" className="form-control" placeholder="Örn: Evcil dostunuzun mutluluğu!" value={template.tagline} onChange={(e) => handleChange('tagline', e.target.value)} disabled={!isEditing} />
                          </div>
                          <div className="col-3">
                             <label className="form-label fw-semibold">Slogan Rengi</label>
                             <input type="color" className="form-control form-control-color w-100" value={template.taglineColor} onChange={(e) => handleChange('taglineColor', e.target.value)} disabled={!isEditing} />
                          </div>

                          <div className="col-12">
                             <label className="form-label fw-semibold">Alt Bilgi / Telif (Footer Text)</label>
                             <input type="text" className="form-control" placeholder="Copyright © 2026 Şirket A.Ş." value={template.footerText} onChange={(e) => handleChange('footerText', e.target.value)} disabled={!isEditing} />
                          </div>

                          <div className="col-12 col-md-6">
                             <label className="form-label fw-semibold">Destek Telefonu</label>
                             <input type="tel" className="form-control" placeholder="0850 555 5555" value={template.supportPhone} onChange={(e) => handleChange('supportPhone', e.target.value)} disabled={!isEditing} />
                          </div>
                          <div className="col-12 col-md-6">
                             <label className="form-label fw-semibold">Destek E-postası</label>
                             <input type="email" className="form-control" placeholder="destek@sirketiniz.com" value={template.supportEmail} onChange={(e) => handleChange('supportEmail', e.target.value)} disabled={!isEditing} />
                          </div>
                       </div>
                    </form>
                 </div>
              </div>
           </div>

           {/* LIVE PREVIEW (Right Side) */}
           <div className="col-12 col-xl-7">
              <div className="card shadow-sm border-0 sticky-top" style={{ top: '140px', zIndex: 1000 }}>
                <div className="card-header bg-dark text-white py-2 d-flex align-items-center rounded-top">
                   <div className="mac-buttons me-3 d-flex gap-1">
                      <div className="rounded-circle bg-danger" style={{width:'10px', height:'10px'}}></div>
                      <div className="rounded-circle bg-warning" style={{width:'10px', height:'10px'}}></div>
                      <div className="rounded-circle bg-success" style={{width:'10px', height:'10px'}}></div>
                   </div>
                   <small className="ms-2 opacity-75">Şablon Önizlemesi — Üst ve alt kısım tüm maillerde aynıdır</small>
                </div>
                <div className="card-body p-3" style={{ backgroundColor: '#f4f5f7', overflowY: 'auto', maxHeight: '75vh' }}>

                   <div style={{
                     maxWidth: '500px',
                     margin: '0 auto',
                     backgroundColor: '#ffffff',
                     borderRadius: '8px',
                     overflow: 'hidden',
                     boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                     border: '1px solid #eaebec',
                     fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                     fontSize: '14px',
                     color: '#333333'
                   }}>

                     {/* ── HEADER (controlled by this template) ── */}
                     <div style={{
                       backgroundColor: template.secondaryColor,
                       padding: '32px 30px',
                       textAlign: 'center',
                       borderBottom: `3px solid ${template.primaryColor}`,
                       transition: 'all 0.3s ease',
                       position: 'relative'
                     }}>
                       {/* "Siz yönetiyorsunuz" badge */}
                       <div style={{ position: 'absolute', top: '8px', right: '8px', backgroundColor: '#22c55e', color: '#fff', fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '99px', letterSpacing: '0.5px' }}>
                         ✓ SİZ YÖNETİYORSUNUZ
                       </div>

                       {template.emailLogoUrl ? (
                         <img src={template.emailLogoUrl} alt="Logo" style={{ maxHeight: '60px', display: 'block', margin: '0 auto' }} />
                       ) : (
                         <div style={{ color: template.primaryColor, fontSize: '26px', fontWeight: 700, letterSpacing: '-0.5px', transition: 'color 0.3s ease' }}>
                           {template.templateName || activeCompany?.name || 'Şirket Adı'}
                         </div>
                       )}
                       {template.tagline ? (
                         <div style={{ color: template.taglineColor || 'rgba(255,255,255,0.80)', fontSize: '14px', fontWeight: 500, marginTop: '10px', transition: 'color 0.2s ease' }}>
                           {template.tagline}
                         </div>
                       ) : (
                         <div style={{ color: 'rgba(255,255,255,0.30)', fontSize: '12px', marginTop: '10px', fontStyle: 'italic' }}>
                           slogan (opsiyonel)
                         </div>
                       )}
                     </div>

                     {/* ── CONTENT ZONE (not controlled by this template) ── */}
                     <div style={{ padding: '28px', backgroundColor: '#fafafc' }}>
                       <div style={{
                         border: '2px dashed #cbd5e1',
                         borderRadius: '8px',
                         padding: '28px 20px',
                         textAlign: 'center',
                         backgroundColor: '#f1f5f9'
                       }}>
                         <div style={{ fontSize: '24px', marginBottom: '8px' }}>✉️</div>
                         <div style={{ fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>
                           Dinamik E-posta İçeriği
                         </div>
                         <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.7, marginBottom: '14px' }}>
                           Bu bölüm mail tipine göre otomatik değişir.<br/>
                           Aşağıdaki tiplerden biri gelir:
                         </div>
                         <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center' }}>
                           {[
                             'Hoş Geldiniz',
                             'Sipariş Alındı',
                             'Kargo Yola Çıktı',
                             'Abonelik İptali',
                             'Fiyat Güncelleme',
                             'İndirim Bitti',
                             'Şifre Sıfırlama',
                             'İade Onayı',
                           ].map(t => (
                             <span key={t} style={{
                               fontSize: '10px',
                               padding: '3px 8px',
                               backgroundColor: '#e2e8f0',
                               color: '#64748b',
                               borderRadius: '99px',
                             }}>{t}</span>
                           ))}
                         </div>
                       </div>
                     </div>

                     {/* ── FOOTER (controlled by this template) ── */}
                     <div style={{ backgroundColor: '#f8f9fa', padding: '24px 28px', textAlign: 'center', borderTop: '1px solid #eaebec', position: 'relative' }}>
                       <div style={{ position: 'absolute', top: '8px', right: '8px', backgroundColor: '#22c55e', color: '#fff', fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '99px', letterSpacing: '0.5px' }}>
                         ✓ SİZ YÖNETİYORSUNUZ
                       </div>

                       {template.footerText ? (
                         <p style={{ color: '#888', fontSize: '12px', margin: '4px 0' }}>{template.footerText}</p>
                       ) : (
                         <p style={{ color: '#bbb', fontSize: '12px', margin: '4px 0', fontStyle: 'italic' }}>
                           Copyright © {new Date().getFullYear()} {activeCompany?.name}
                         </p>
                       )}

                       {(template.supportPhone || template.supportEmail) ? (
                         <p style={{ color: '#555', fontSize: '12px', margin: '10px 0 0', lineHeight: 1.6 }}>
                           {[template.supportPhone, template.supportEmail].filter(Boolean).join(' / ')}
                         </p>
                       ) : (
                         <p style={{ color: '#bbb', fontSize: '11px', margin: '8px 0 0', fontStyle: 'italic' }}>
                           destek telefonu / e-posta (opsiyonel)
                         </p>
                       )}

                       <p style={{ color: '#111', fontSize: '12px', fontWeight: 700, marginTop: '14px', letterSpacing: '-0.2px' }}>
                         {activeCompany?.name || 'Şirket Adı'}
                       </p>
                     </div>

                   </div>

                   {/* Legend */}
                   <div style={{ maxWidth: '500px', margin: '12px auto 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                     <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                       Açıklama
                     </div>
                     {[
                       { color: '#22c55e', label: 'Yeşil rozet — Bu şablonla yönettiğiniz bölümler (header + footer)' },
                       { color: '#94a3b8', label: 'Kesik çizgi alan — İçerik: her mail tipinde sistem tarafından otomatik doldurulur' },
                     ].map((item, i) => (
                       <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                         <div style={{ width: '12px', height: '12px', borderRadius: '99px', backgroundColor: item.color, flexShrink: 0, marginTop: '2px' }} />
                         <span style={{ fontSize: '11px', color: '#64748b', lineHeight: 1.5 }}>{item.label}</span>
                       </div>
                     ))}
                   </div>

                </div>
              </div>
           </div>
        </div>

      </div>
    </ErrorBoundary>
  );
}

export default EmailTemplateSettings;
